import { Service } from '../Service';
import {IParams} from "../IService";
import {Factory} from "../Factory";

export interface IResourceParams extends IParams {
  projectId?:string;
  collectionId?: string;
  collectionIds?: string[];
  page: number;
  limit: number;
  tagFilter;
  columnFilters;
  sortBy?: string;
  sortDirection?: any;
}

export class ResourceService extends Service<any> {
  constructor(resource: any, rootScope: IRootScope, private configService: any, resourceWrapperService: any, private tokenService: any, private notificationService: any, private httpService: any) {
    super({resource, rootScope, resourceWrapperService});

    let baseUrl = configService.getServerBaseUrlForResource();
    let raw = this.resource(`${baseUrl}/project/:projectId/collection/:collectionId/resource/:id`,
      {id: "@id", projectId: "@projectId", collectionId: "@collectionId"},
      {
        update: {method: "PUT"},
        query: {method: "POST", params: {page: 1, limit: 50}}
      });

    this.itemResource = this.resourceWrapperService.WrapActions(raw);

    this.RegisterCompleteEventListener("FeedService.RedactionEnd", async (event, message) => message);

    rootScope.$on("FeedService.ExtractedConversationProcessing", (event, message) => {
      notificationService.addSuccess({
        title: 'Extracted Conversation saved and processing.',
        description: message.description,
        timeout: 5000
      });
    });
  }

  public async LoadOne(params: IResourceParams) {
    return await this.itemResource.get({
      id: params.id,
      collectionId: params.collectionId,
      projectId: params.projectId
    }).$promise;
  }

  public async FindAll(params: IResourceParams) {
    const resourcePage = await this.itemResource.query({
      projectId: params.projectId,
      collectionId: params.collectionId ?? params.collectionIds.join(","),
      page: params.page,
      limit: params.limit,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
      tagFilter: params.tagFilter
    }, params.columnFilters).$promise;

    return resourcePage as any;
  }

  public LaunchFullView(config: any) {
    if (window.__resourceViewerStore) {
      window.__resourceViewerStore.getState().open({
        projectId: config.projectId,
        collectionId: config.collectionId,
        resourceId: config.resourceId,
      });
    } else {
      this.rootScope.$broadcast("ResourceService.LaunchFullView", config);
    }
  }

  public async GetRedactions(projectId: string, collectionId: string, resourceId: string) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/redactions`;

    const response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: ""
    });

    return response.data;
  }

  public async SaveRedactions(projectId: string, collectionId: string, resourceId: string, pageRedactions: any[]) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/redactions`;

    const response = await this.httpService.$http({
      method: "POST",
      url: url,
      data: angular.toJson(pageRedactions)
    });

    return response.data;
  }

  public async DeleteRedactions(projectId: string, collectionId: string, resourceId: string) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/redactions`;

    const response = await this.httpService.$http({
      method: "DELETE",
      url: url
    });

    return response.data;
  }

  public GetDownloadUri(projectId: string, collectionId: string, resourceId: string) {
    return `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/download?client_id=${this.configService.getClientId()}&access_token=${this.tokenService.GetAccessToken()}`;
  }

  public async FindNamedEntities(projectId: string, collectionId: string, resourceId: string, page: number, limit: number, groupColumn, groupKey) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/namedEntities`;

    const response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: "",
      params: {
        page,
        limit,
        groupColumn,
        groupKey
      }
    });

    return response.data;
  }

  public async GetChildren(projectId: string, collectionId: string, resourceId: string, page: number, limit: number) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/children`;

    const response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: "",
      params: {
        page,
        limit
      }
    });

    return response.data;
  }

  public async FindSimilar(projectId: string, collectionId: string, resourceId: string, page: number, limit: number) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/similar`;

    const response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: "",
      params: {
        page,
        limit
      }
    });

    return response.data;
  }

  public async FindThread(projectId: string, collectionId: string, resourceId: string) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/thread`;

    const response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: ""
    });

    return response.data;
  }

  public async FindHighlights(projectId: string, collectionId: string, resourceId: string, query: any) {
    let url, response;

    if (angular.isString(query)) {
      // Basic Search
      url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/highlights`;
      response = await this.httpService.$http({
        method: "POST",
        url: url,
        data: "",
        params: {"query": query}
      });
    } else {
      // Advanced Search
      url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/highlights_adv`;
      response = await this.httpService.$http({
        method: "POST",
        url: url,
        data: query
      });
    }

    return response.data;
  }

  public async FindHighlightsForTerm(projectId: string, collectionId: string, resourceId: string, searchTermId: string) {
    let url, response;

    url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/terms/${searchTermId}/highlights`;
    response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: "",
    });

    return response.data;
  }

  public Reprocess(resource: any, callback: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/resource/${resource.id}/reprocess`;

    return this.httpService.$http({
      method: "POST",
      url: url
    }).success(() => {
      this.notificationService.addSuccess({
        title: "Reprocessing started",
        description: "",
        timeout: 2500
      });
      if (callback) callback();
    }).error(() => {
      this.notificationService.addError({
        title: "Error Reprocessing",
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });
      if (callback) callback();
    });
  }

  public ReplacePdf(resource: any, file: any, isRedacted: boolean, progressCallback: Function, successCallback: Function, errorCallback: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/resource/${resource.id}/pdfreplace?isRedacted=${isRedacted}`;

    const formData = new FormData();

    if (file) {
      formData.append("file", file);
    }

    progressCallback(0, 0, 0);

    this.httpService.$ajax(url, {
      type: "POST",
      data: formData,
      cache: false,
      processData: false,
      contentType: false,
      xhr: () => {
        let myXhr = $.ajaxSettings.xhr();
        if (myXhr.upload) {
          myXhr.upload.addEventListener("progress", (ev: any) => {
            if (ev.lengthComputable) {
              let percentUploaded = Math.floor(ev.loaded * 100 / ev.total);
              progressCallback(ev.loaded, ev.total, percentUploaded);
            }
          }, false);
        }
        return myXhr;
      },
      success: successCallback,
      error: errorCallback
    });
  }

  public async GetConversations(resource: any, page: number, limit: number) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/resource/${resource.id}/conversations`;

    const response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: "",
      params: {
        page,
        limit
      }
    });

    return response.data;
  }

  public async GetMessages(resource: any, messageId: number, page: number, limit: number) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/resource/${resource.id}/messageId/${messageId}/messages`;

    const response = await this.httpService.$http({
      method: "GET",
      url: url,
      data: "",
      params: {
        page,
        limit
      }
    });

    return response.data;
  }

  public GetConversationIndex(resource: any) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/resource/${resource.id}/messageId/${resource.chatMessageId}/index`;

    return this.httpService.$http({
      method: "GET",
      url: url,
      data: ""
    });
  }

  public GetMessageIndex(resource: any) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/resource/${resource.id}/conversationId/${resource.chatConversationId}/messageId/${resource.chatMessageId}/index`;

    return this.httpService.$http({
      method: "GET",
      url: url,
      data: ""
    });
  }

  public SaveConversation(resource: any, rows: Array<any>) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/save`;

    this.notificationService.addSuccess({
      title: 'Extracting Conversation Started',
      description: "",
      timeout: 5000
    });

    return this.httpService.$http({
      method: "POST",
      url: url,
      data: _.pluck(rows, "resourceId")
    });
  }

  public SaveEntireConversation(resource: any, resourceId: String) {
    let url = `${this.configService.getServerBaseUrl()}/project/${resource.projectId}/collection/${resource.collectionId}/resource/${resourceId}/save`;

    this.notificationService.addSuccess({
      title: 'Extracting Conversation Started',
      description: "",
      timeout: 5000
    });

    return this.httpService.$http({
      method: "POST",
      url: url,
      data: null
    });
  }
}

frontendApp.factory("ResourceService", ['$resource', '$rootScope', 'ConfigService', 'ResourceWrapperService', 'TokenService', 'NotificationService', 'HttpService', Factory.Create(ResourceService)]);
